import { Component } from './live-codec.js';
import { readResource, resourceValue, resourceProperties, resourceCoordinate } from './vgui-res.js';

const classes = ['—', 'Scout', 'Sniper', 'Soldier', 'Demoman', 'Medic', 'Heavy', 'Pyro', 'Spy', 'Engineer'];
const fallback = {
    scores: { wide: '640', tall: '448', score_width: '30', class_width: '65', ping_width: '30' },
    blueplayerlist: { xpos: '5', ypos: '67', wide: '310', tall: '280', linespacing: '18', linegap: '5' },
    redplayerlist: { xpos: '325', ypos: '67', wide: '310', tall: '280', linespacing: '18', linegap: '5' },
    bluescorebg: { xpos: '0', ypos: '9', wide: '315', tall: '58' },
    redscorebg: { xpos: '325', ypos: '9', wide: '315', tall: '58' },
    spectators: { xpos: '11', ypos: '357', wide: '618', tall: '20' },
    servertimeleft: { xpos: '11', ypos: '385', wide: '300', tall: '16' },
    mapname: { xpos: '315', ypos: '385', wide: '314', tall: '16' },
};
export class ScoreboardState {
    constructor() { this.players = new Map(); this.revision = 0; }
    reset() { this.players.clear(); this.mapTime = null; this.mapTimeKey = null; this.revision++; }
    apply(message) {
        let changed = false;
        if (message.replace) { this.players.clear(); this.mapTime = null; this.mapTimeKey = null; changed = true; }
        if (message.deleted?.includes(this.mapTimeKey)) { this.mapTime = null; this.mapTimeKey = null; changed = true; }
        for (const key of message.deleted ?? []) changed = this.players.delete(key) || changed;
        for (const entity of message.changes ?? []) {
            const player = entity.components?.[Component.Player];
            const score = entity.components?.[Component.Scoreboard];
            if (score?.mapTime) {
                this.mapTime = { ...score.mapTime, receivedAt: performance.now() }; this.mapTimeKey = entity.key;
                changed = true; continue;
            }
            const old = this.players.get(entity.key);
            if (!player && !score) continue;
            const row = { ...(old ?? { index: entity.index, name: `Player ${entity.index}`, score: null, ping: null }),
                ...(score ?? {}), ...(player ? { team: player.team, playerClass: player.playerClass, alive: !!(player.state & 1) } : {}) };
            if (!old || Object.keys(row).some(key => row[key] !== old[key])) {
                this.players.set(entity.key, row); changed = true;
            }
        }
        if (changed) this.revision++;
        return changed;
    }
    teams() {
        const teams = { blue: [], red: [], spectators: [] };
        for (const player of this.players.values()) teams[player.team === 3 ? 'blue' : player.team === 2 ? 'red' : 'spectators'].push(player);
        for (const list of Object.values(teams)) list.sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity) || a.index - b.index);
        return teams;
    }
}
// Mirrors CTFClientScoreBoardDialog::UpdateServerTimeLeft (including hours).
export function mapTimeText(time, now = performance.now(), live = true) {
    if (!time) return '';
    if (!time.limited) return 'No time limit';
    const seconds = Math.max(0, time.seconds - (live ? Math.floor(Math.max(0, now - time.receivedAt) / 1000) : 0));
    if (!seconds) return 'Map changes on round end';
    const minutes = String(Math.floor(seconds % 3600 / 60)).padStart(2, '0'), rest = String(seconds % 60).padStart(2, '0');
    return `Time left: ${seconds >= 3600 ? `${Math.floor(seconds / 3600)}:` : ''}${minutes}:${rest}`;
}
function element(tag, className, text) {
    const node = document.createElement(tag); node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}
export class LiveScoreboard {
    constructor({ root, canvas, button, cache, diagnostic = () => {} }) {
        this.root = root; this.canvas = canvas; this.button = button; this.cache = cache; this.diagnostic = diagnostic;
        this.state = new ScoreboardState(); this.layout = fallback; this.listeners = []; this.held = false; this.pinned = false;
        this.panel = element('section', 'live-scoreboard-panel'); this.panel.setAttribute('aria-label', 'Server scoreboard');
        root.append(this.panel);
        this.on(window, 'keydown', event => {
            if (event.code === 'Tab' && (document.pointerLockElement === canvas || document.activeElement === canvas)) {
                event.preventDefault(); this.held = true; this.show();
            } else if (event.code === 'Escape') this.hide();
        });
        this.on(window, 'keyup', event => { if (event.code === 'Tab') { this.held = false; this.show(); } });
        this.on(window, 'blur', () => this.hide());
        this.on(canvas, 'blur', () => { this.held = false; this.show(); });
        this.on(document, 'visibilitychange', () => { if (document.hidden) this.hide(); });
        this.on(document, 'pointerlockchange', () => { if (document.pointerLockElement !== canvas) { this.held = false; this.show(); } });
        this.on(button, 'click', () => { this.pinned = !this.pinned; this.show(); });
        this.resize = new ResizeObserver(() => this.fit()); this.resize.observe(root.parentElement);
        this.build(); this.show();
        this.timer = setInterval(() => {
            if (this.root.hidden) return;
            const metrics = this.metrics?.(), started = metrics ? performance.now() : 0;
            this.renderTime();
            metrics?.record('hud', performance.now() - started);
        }, 250);
    }
    on(target, type, listener) { target.addEventListener(type, listener); this.listeners.push([target, type, listener]); }
    show() {
        this.root.hidden = !(this.held || this.pinned);
        this.button.setAttribute('aria-expanded', String(!this.root.hidden));
        if (!this.root.hidden) { this.render(); this.fit(); }
    }
    hide() { this.held = this.pinned = false; this.show(); }
    reset() { this.state.reset(); this.hide(); this.renderedRevision = -1; }
    metadata({ serverName, mapName, status, live } = {}) {
        if (live !== undefined && live !== this.live) {
            if (this.state.mapTime && this.live) {
                const time = this.state.mapTime;
                time.seconds = Math.max(0, time.seconds - Math.floor((performance.now() - time.receivedAt) / 1000));
            }
            if (this.state.mapTime) this.state.mapTime.receivedAt = performance.now();
            this.live = live;
        }
        if (serverName !== undefined) this.serverName = serverName;
        if (mapName !== undefined) this.mapName = mapName;
        if (status !== undefined) this.status = status;
        this.renderedRevision = -1; if (!this.root.hidden) this.render();
    }
    apply(message) { if (this.state.apply(message) && !this.root.hidden) this.render(); }
    async load(assetSet) {
        if (!assetSet || this.assetSet === assetSet) return;
        this.assetSet = assetSet; this.abort?.abort(); const abort = this.abort = new AbortController();
        this.layout = fallback; this.build();
        try {
            const entries = await readResource('resource/ui/scoreboard.res', path => this.cache.fetch(
                `/assets/${path}?assetSet=${encodeURIComponent(assetSet)}`, abort.signal));
            if (abort.signal.aborted) return;
            const root = entries.find(entry => Array.isArray(entry.value) && resourceValue(entry.value, 'scores'))?.value;
            if (!root) throw new Error('Scoreboard resource has no scores panel');
            this.layout = Object.fromEntries(Object.keys(fallback).map(key => [key, {
                ...fallback[key], ...resourceProperties(resourceValue(root, key)),
            }]));
            this.build(); this.show();
        } catch (error) {
            if (!abort.signal.aborted) this.diagnostic(`Scoreboard layout unavailable; using the default layout. ${error.message}`);
        }
    }
    bounds(node, key) {
        const props = this.layout[key], width = this.width, height = this.height;
        const number = (name, parent, backup) => Math.max(0, Math.min(parent, resourceCoordinate(props[name], parent, backup)));
        Object.assign(node.style, {
            left: `${resourceCoordinate(props.xpos, width)}px`, top: `${resourceCoordinate(props.ypos, height)}px`,
            width: `${number('wide', width, width)}px`, height: `${number('tall', height, height)}px`,
        });
    }
    build() {
        const dimension = (key, backup) => Math.max(200, Math.min(1280, Number(this.layout.scores[key]) || backup));
        this.width = dimension('wide', 640); this.height = dimension('tall', 448);
        this.panel.style.width = `${this.width}px`; this.panel.style.height = `${this.height}px`;
        this.panel.replaceChildren(); this.sections = {};
        for (const team of ['blue', 'red']) {
            const header = element('header', `live-scoreboard-team live-scoreboard-${team}`);
            this.bounds(header, `${team}scorebg`); this.panel.append(header);
            const title = element('h2', '', team === 'blue' ? 'BLU' : 'RED'), count = element('span', ''); header.append(title, count);
            const container = element('div', `live-scoreboard-list live-scoreboard-${team}`); this.bounds(container, `${team}playerlist`);
            const table = element('table', ''); table.setAttribute('aria-label', `${team === 'blue' ? 'BLU' : 'RED'} players`);
            const cols = element('colgroup', '');
            // TF2's class column holds an icon. Use a readable class name instead.
            for (const size of [null, Math.max(64, Number(this.layout.scores.class_width) || 15),
                Math.max(40, Number(this.layout.scores.score_width) || 25), Math.max(40, Number(this.layout.scores.ping_width) || 25)]) {
                const col = element('col', ''); if (size) col.style.width = `${Math.min(100, size)}px`; cols.append(col);
            }
            const head = element('thead', ''), row = element('tr', '');
            for (const label of ['Name', 'Class', 'Score', 'Ping']) { const th = element('th', '', label); th.scope = 'col'; row.append(th); }
            head.append(row); const body = element('tbody', '');
            const props = this.layout[`${team}playerlist`];
            const rowHeight = Math.max(18, Math.min(40, (Number(props.linespacing) || 18) + (Number(props.linegap) || 0)));
            body.style.setProperty('--scoreboard-row-height', `${rowHeight}px`);
            table.append(cols, head, body); container.append(table); this.panel.append(container);
            this.sections[team] = { count, body, header, container };
        }
        this.spectators = element('div', 'live-scoreboard-spectators'); this.bounds(this.spectators, 'spectators');
        this.map = element('div', 'live-scoreboard-map'); this.bounds(this.map, 'mapname');
        this.time = element('div', 'live-scoreboard-time'); this.bounds(this.time, 'servertimeleft');
        this.server = element('div', 'live-scoreboard-server');
        this.notice = element('div', 'live-scoreboard-notice');
        this.panel.append(this.spectators, this.map, this.time, this.server, this.notice);
        this.renderedRevision = -1; this.render(); this.fit();
    }
    renderTime() { this.time.textContent = mapTimeText(this.state.mapTime, performance.now(), this.live); }
    render() {
        this.renderTime();
        if (this.renderedRevision === this.state.revision) return;
        this.renderedRevision = this.state.revision;
        const teams = this.state.teams();
        for (const team of ['blue', 'red']) {
            const { count, body } = this.sections[team]; count.textContent = `${teams[team].length} ${teams[team].length === 1 ? 'player' : 'players'}`;
            body.replaceChildren(...teams[team].map(player => {
                const row = element('tr', player.alive ? '' : 'live-scoreboard-dead');
                for (const text of [player.name, classes[player.playerClass] ?? '—', player.score ?? '—', player.bot ? 'BOT' : player.ping ?? '—']) row.append(element('td', '', String(text)));
                row.firstChild.title = `${player.name}${player.alive ? '' : ' (dead)'}`;
                return row;
            }));
        }
        this.spectators.textContent = `Spectators: ${teams.spectators.map(player => player.name).join(', ') || 'None'}`;
        this.spectators.title = this.spectators.textContent;
        this.map.textContent = this.mapName ?? ''; this.server.textContent = this.serverName ?? 'Server scoreboard';
        this.notice.textContent = this.status ?? (this.state.players.size ? '' : 'Waiting for player data…');
    }
    fit() {
        const viewer = this.root.parentElement.getBoundingClientRect();
        const toolbar = this.root.parentElement.querySelector('#toolbar')?.getBoundingClientRect();
        // Reserve the actual wrapped toolbar and status areas before scaling,
        // so both the desktop resource layout and stacked mobile layout fit below it.
        const top = toolbar ? toolbar.bottom - viewer.top + 12 : 12;
        const available = Math.max(100, viewer.height - top - 48);
        const compact = viewer.width < 600;
        this.panel.classList.toggle('live-scoreboard-compact', compact);
        this.panel.style.top = `${top + available / 2}px`;
        this.panel.style.width = `${compact ? viewer.width - 24 : this.width}px`;
        this.panel.style.height = `${compact ? Math.min(620, available) : this.height}px`;
        if (compact) {
            const height = Math.min(620, available), width = viewer.width - 24, block = (height - 100) / 2;
            for (const [index, team] of ['blue', 'red'].entries()) {
                const { header, container } = this.sections[team];
                Object.assign(header.style, { left: '8px', top: `${8 + index * block}px`, width: `${width - 16}px`, height: '45px' });
                Object.assign(container.style, { left: '8px', top: `${53 + index * block}px`, width: `${width - 16}px`, height: `${Math.max(25, block - 49)}px` });
            }
            Object.assign(this.spectators.style, { left: '11px', top: `${height - 84}px`, width: `${width - 22}px`, height: '20px' });
            Object.assign(this.time.style, { left: '11px', top: `${height - 66}px`, width: `${width - 22}px`, height: '18px' });
            Object.assign(this.map.style, { left: `${width / 2}px`, top: `${height - 46}px`, width: `${width / 2 - 11}px`, height: '20px' });
        } else {
            for (const team of ['blue', 'red']) {
                this.bounds(this.sections[team].header, `${team}scorebg`);
                this.bounds(this.sections[team].container, `${team}playerlist`);
            }
            this.bounds(this.spectators, 'spectators'); this.bounds(this.map, 'mapname'); this.bounds(this.time, 'servertimeleft');
        }
        const scale = compact ? 1 : Math.max(0.1, Math.min(1.5, (viewer.width - 24) / this.width, available / this.height));
        this.panel.style.transform = `translate(-50%, -50%) scale(${scale})`;
    }
    dispose() { clearInterval(this.timer); this.abort?.abort(); this.resize.disconnect(); for (const args of this.listeners) args[0].removeEventListener(args[1], args[2]); this.root.replaceChildren(); }
}
